import { Basic } from '../basic.js';
import styles from './uploadfile.css'

const template = () => `
    <style>
       ${styles}
    </style>
    <form class="uploadfile">
        <div class="uploadfile__title">${i18next.t("upload_upload_file")}.</div>
        <div class="uploadfile__drag-file-area">
            <div class="uploadfile__info">${i18next.t("upload_drag_and_drop")}</div>
        </div>
        <div class="uploadfile__filename"></div>
        <div class="uploadfile__footer">
            <button class="uploadfile__btn uploadfile__btn--upload uploadfile__btn--hidden" type="button">${i18next.t("upload_download")}</button> 
            <label class="uploadfile__btn" for="upload-input">
            ${i18next.t("upload_select")}
                <input class="uploadfile__upload-input" id="upload-input" type="file" /> 
            </label>
        </div> 
    </form>
`;

/**
 * @class MSUploadFile
 * @extends Basic
 * @classdesc Контрол "Загрузка файла"
 * */
export class MSUploadFile extends Basic {
    static get observedAttributes() {
        return super.observedAttributes.concat([
            "autoload",
            "busy",
            "completed",
            "errorcode",
            "fullpath",
        ]);
    }

    // Автоматическая загрузка файла.
    // Значение по умолчанию false.
    // Если false, тогда после нажатия на кнопку "Выбрать файл" необходимо отобразить кнопку "Загрузить".
    // Если true, тогда загрузка происходит автоматически после выбора файла.
    get autoload() {
        return this._autoload ?? false;
    }
    set autoload(value) {
        this._autoload = this._toBool(value);
    }

    // Идет процесс загрузки.
    // Значение по умолчанию false. После начала загрузки файла меняется на true.
    // Когда файл загружен меняется на false и сохраняется до тех пор, пока пользователь не выберет новый файл.
    // В начале следующей загрузки значение меняется на true.
    get busy() {
        return this._busy ?? false;
    }
    set busy(value) {
        this._busy = this._toBool(value);
    }

    // Загрузка файла завершена.
    // Значение по умолчанию false. По нажатию на кнопку "Загрузить" completed переводится в false.
    // Если загрузка произошла успешно, то значение меняется на true.
    // Если сервер вернул ошибку, то значение остаётся в false.
    get completed() {
        return this._completed ?? false;
    }
    set completed(value) {
        this._completed = this._toBool(value);
    }

    // Код ошибки в случае неудачной загрузки файла на сервер
    get errorcode() {
        return this._errorcode;
    }
    set errorcode(value) {
        this._errorcode = value;
    }

    // Полный путь с именем загруженного файла
    get fullpath() {
        return this._fullpath;
    }
    set fullpath(value) {
        this._fullpath = value;
    }

    constructor() {
        super();
        const shadowRoot = this.attachShadow({ mode: 'open' });
        shadowRoot.innerHTML = template();
    
        this.root = this.shadowRoot.querySelector(".uploadfile");
        this.uploadInput = this.root.querySelector('#upload-input');
        this.tooltip = i18next.t("upload_drag");
    
        this.updloadBtn = this.root.querySelector('.uploadfile__btn--upload');
        this.uploadHandler = async (e) => {
            await this.uploadFile(this.uploadInput.files[0]);
        };
        this.updloadBtn.addEventListener('click', this.uploadHandler);
    
        this.fileChangeHandler = async (e) => {
            this.onFileAdded();
        };
        this.uploadInput.addEventListener('change', this.fileChangeHandler);
    
        this.dragHandlers = {
            dragover: (e) => {
                e.preventDefault();
                this.root.classList.add('uploadfile--dragging');
            },
            dragleave: (e) => {
                e.preventDefault();
                this.root.classList.remove('uploadfile--dragging');
            },
            drop: (e) => {
                e.preventDefault();
                this.root.classList.remove('uploadfile--dragging');
                this.uploadInput.files = e.dataTransfer.files;
                this.onFileAdded();
            }
        };
    
        ["drag", "dragstart", "dragend", "dragenter", "drop"].forEach(evt =>
            this.root.addEventListener(evt, this.dragHandlers[evt] || (() => {}))
        );
    }

    addDragNDrop() {
        ["drag", "dragstart", "dragend", "dragover", "dragenter", "dragleave", "drop"].forEach(evt =>
            this.root.addEventListener(evt, e => {
                e.preventDefault();

                switch (evt) {
                    case "dragover": {
                        this.root.classList.add('uploadfile--dragging');
                        break;
                    }
                    case "dragleave": {
                        this.root.classList.remove('uploadfile--dragging');
                        break;
                    }
                    case "drop": {
                        this.root.classList.remove('uploadfile--dragging');
                        this.uploadInput.files = e.dataTransfer.files;
                        this.onFileAdded();
                        break;
                    }
                }
            })
        );
    }

    async onFileAdded() {
        this.root.classList.add('uploadfile--added');
        this.root.querySelector('.uploadfile__title').textContent = i18next.t("upload_file_added");
        const file = this.uploadInput.files[0];
        const fileName = this.root.querySelector('.uploadfile__fileName');
        fileName.textContent = file.name;
        fileName.classList.remove('uploadfile__fileName--success', 'uploadfile__fileName--error');
        this.autoload
            ? await this.uploadFile(file)
            : this.setUploadBtnVisibility(true);
        this.SetParameter("completed", false);
    }

    async uploadFile(file) {
        this.SetParameter("busy", true);
        this.SetParameter("completed", false);
        const uploadFileRequest = await fetch(`//${window.location.hostname}:${window.location.port}/Methods/Upload`, {
            method: "POST",
            headers: {
                "session-id": $sw.sessionId,
            },
            body: file
        });
        this.completeFileUploading(await uploadFileRequest.json());
    }

    completeFileUploading({ code, fullPath }) {
        this.SetParameter("busy", false);
        this.SetParameter("errorcode", code);
        this.SetParameter("fullpath", fullPath);
        const fileName = this.root.querySelector('.uploadfile__fileName');
        if (code === 0) {
            this.SetParameter("completed", true);
            this.root.querySelector('.uploadfile__title').textContent = i18next.t("upload_file_complete");
            fileName.classList.add('uploadfile__fileName--success')
        } else {
            this.root.querySelector('.uploadfile__title').textContent = i18next.t("upload_file_error");
            fileName.classList.add('uploadfile__fileName--error')
        }
        this.setUploadBtnVisibility(false);
        this.uploadInput.value = "";
    }

    setUploadBtnVisibility(isVisible) {
        this.updloadBtn.classList.toggle('uploadfile__btn--hidden', !isVisible);
    }

    disconnectedCallback() {
        ["drag", "dragstart", "dragend", "dragover", "dragenter", "dragleave", "drop"].forEach(evt => {
            if (this.dragHandlers[evt]) this.root.removeEventListener(evt, this.dragHandlers[evt]);
        });
        if (this.uploadHandler) this.updloadBtn.removeEventListener('click', this.uploadHandler);
        if (this.fileChangeHandler) this.uploadInput.removeEventListener('change', this.fileChangeHandler);
        super.disconnectedCallback();
    }
}